import { memo, useEffect, useCallback, useMemo } from "react";
import "./css/Sidebar.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "../Context/AppContext";
import { clearTokens } from "../auth/authStorage";
import { useTranslation } from "react-i18next";
import { isBusinessOrExecutiveMembership } from "../utils/membershipTier";

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

function Sidebar({ isOpen, toggleSidebar }: SidebarProps) {
  const { t } = useTranslation();
  const { user } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = useCallback(async () => {
    clearTokens();
    navigate("/", { replace: true });
  }, [navigate]);

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      if (window.innerWidth < 1024) {
        toggleSidebar();
      }
    },
    [navigate, toggleSidebar]
  );

  const isActive = useCallback(
    (path: string) => location.pathname.startsWith(path),
    [location.pathname]
  );

  useEffect(() => {
    if (!isOpen || window.innerWidth >= 1024) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const sidebarElement = document.querySelector(".sidebar");
      const toggleButton = document.querySelector(".navbar-toggler");

      if (
        sidebarElement &&
        !sidebarElement.contains(event.target as Node) &&
        toggleButton &&
        !toggleButton.contains(event.target as Node)
      ) {
        toggleSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, toggleSidebar]);

  const isAdmin = useMemo(() => user?.user?.isAdmin === 1, [user?.user?.isAdmin]);

  const showMuslimInvest = useMemo(
    () => isBusinessOrExecutiveMembership(user?.user?.membershipName),
    [user?.user?.membershipName],
  );

  const menuItems = useMemo(
    () => [
      { path: "/updates", label: t("sidebar.updates") },
      { path: "/dashboard", label: t("sidebar.dashboard") },
      { path: "/event-booking", label: t("sidebar.events") },
      ...(showMuslimInvest
        ? [{ path: "/investing", label: t("sidebar.muslimInvest") }]
        : []),
      { path: "/partners", label: t("sidebar.partners") },
      { path: "/messages", label: t("sidebar.messages") },
      { path: "/connections", label: t("sidebar.connections") },
      { path: "/contact-us", label: t("sidebar.contactUs") },
      { path: "/my-account", label: t("sidebar.myAccount") },
    ],
    [t, showMuslimInvest],
  );

  return (
    <div className={`sidebar gradient-bg text-white ${isOpen ? "open" : ""}`}>
      <ul className="navbar-nav me-auto py-3">
        {menuItems.map((item) => (
          <li key={item.path} className="nav-item btn-bg">
            <button
              className={`nav-link w-100 ${isActive(item.path) ? "active" : ""}`}
              onClick={() => handleNavigate(item.path)}
            >
              {item.label}
            </button>
          </li>
        ))}
        {isAdmin && (
          <li className="nav-item btn-bg">
            <button
              className={`nav-link w-100 ${isActive("/admin") ? "active" : ""}`}
              onClick={() => handleNavigate("/admin")}
            >
              {t("sidebar.admin")}
            </button>
          </li>
        )}
      </ul>
      <div className="ms-auto d-lg-none login-button">
        <button
          className="nav-link border text-center w-100 btn-bg border-white rounded px-3 py-1 mt-2"
          onClick={handleLogout}
        >
          {t("sidebar.logout")}
        </button>
      </div>
    </div>
  );
}

Sidebar.displayName = "Sidebar";

export default memo(Sidebar);
